// DRV-5b(ii) (#2883) — the Drive filing badge, in the artefact viewer header
// only (D-3: ArtefactDetailContent, after the status pill). Not in chat
// cards, list views, the client dashboard, amendments or the member
// dashboard; list icons are #3263.
//
// The copy, the actions and who sees them are decided in filingState.ts;
// reads and polling in useStorageStatusPoll.ts. This file renders, and runs
// File now / Retry:
// - POST /artefacts/:id/storage-file (TENANT_ADMIN only server-side; the
//   buttons are hidden below admin rank, D-1). A 202 restarts the poll.
// - A 403/404 (R2-1): the badge goes, as for a poll 403/404.
// - Any 409 (D-4): one immediate refresh, then the neutral line. Message
//   text is never read or shown; only `reason: 'no_approved_version'` gets
//   its own wording (the #3251 text).
// - Every Drive link goes through safeDriveUrl (filingState.ts); a link that
//   fails it is not rendered.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { formatTimeAgo } from '../../lib/dashboardFormat';
import { formatDriveDate } from '../../lib/driveIntegration';
import {
  describeFiling,
  REVOKED_VERSION_NOTICE,
  SETTINGS_DRIVE_PATH,
  SETTINGS_STORAGE_PATH,
  STALE_NOTICE,
  type FilingAction,
  type FilingTone,
} from './filingState';
import { useStorageStatusPoll } from './useStorageStatusPoll';

interface Props {
  artefactId: string;
  /** TENANT_ADMIN rank (isAdminRole). */
  isAdmin: boolean;
  /** The artefact's latest version is approved or handed off. */
  latestApproved: boolean;
}

export const CONFLICT_LINE = "Couldn't file just now; status updated";
export const PRESS_FAILED_LINE = "Couldn't file just now. Try again.";

const ACTION_LABEL: Record<FilingAction, string> = {
  file_now: 'File now',
  retry:    'Retry',
  recreate: 'Re-create in Drive',
};

type PressOutcome = null | 'conflict' | 'failed';

export default function FilingBadge({ artefactId, isAdmin, latestApproved }: Props) {
  const { status, readAt, gone, restart, markGone } = useStorageStatusPoll(artefactId);
  // Press state is keyed by the artefact it was made on (R1-3), so it never
  // shows on another artefact even without the viewer's remount.
  const [pressingFor, setPressingFor] = useState<string | null>(null);
  const [outcomeFor, setOutcomeFor] = useState<{ id: string; outcome: PressOutcome } | null>(null);
  const [noApprovedFor, setNoApprovedFor] = useState<string | null>(null);
  // R2-3: on an artefact change, drop the old id's press state (not kept for
  // a return to it). A press still in flight for the old id can't touch the
  // new one: every write below is tagged with the id it was made on.
  const [shownId, setShownId] = useState(artefactId);
  if (shownId !== artefactId) {
    setShownId(artefactId);
    setPressingFor(null);
    setOutcomeFor(null);
    setNoApprovedFor(null);
  }
  const pressing = pressingFor === artefactId;
  const outcome: PressOutcome = outcomeFor?.id === artefactId ? outcomeFor.outcome : null;
  const noApprovedVersion = noApprovedFor === artefactId;

  if (gone || !status) return null;
  const view = describeFiling(status, { isAdmin, now: readAt, latestApproved, noApprovedVersion });
  if (!view) return null;

  function press() {
    const id = artefactId;
    setPressingFor(id);
    setOutcomeFor(null);
    apiFetch(`/artefacts/${encodeURIComponent(id)}/storage-file`, { method: 'POST' })
      .then(() => restart())
      .catch((err: unknown) => {
        // R2-1: refused (403) or not there (404), as a poll 403/404: the badge
        // goes, reads stop, no text, no retry.
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          markGone(id);
          return;
        }
        if (err instanceof ApiError && err.status === 409) {
          if (err.body?.reason === 'no_approved_version') setNoApprovedFor(id);
          else setOutcomeFor({ id, outcome: 'conflict' });
          restart();
          return;
        }
        setOutcomeFor({ id, outcome: 'failed' });
      })
      .finally(() => setPressingFor((p) => (p === id ? null : p)));
  }

  const actionButton = view.action && (
    <button
      type="button"
      data-testid="filing-badge-action"
      data-action={view.action}
      onClick={press}
      disabled={pressing}
      style={actionStyle}
    >
      {pressing ? 'Filing…' : ACTION_LABEL[view.action]}
    </button>
  );
  // Row 10: when stale, the one File now sits in the notice.
  const actionInNotice = view.stale === 'notice' && view.action === 'file_now';
  const filedAgo = view.filedAt ? formatTimeAgo(view.filedAt, readAt) : '';

  return (
    <span data-testid="filing-badge" data-row={view.row} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span data-testid="filing-badge-label" style={{ ...pillStyle, ...TONE_STYLE[view.tone] }} className="whitespace-nowrap">
        {view.label}
        {view.row === 4 && formatDriveDate(view.until) && <> until {formatDriveDate(view.until)}</>}
        {filedAgo && <> · {filedAgo}</>}
      </span>
      {view.links.map((l) => (
        <a
          key={`${l.format}-${l.href}`}
          data-testid="filing-badge-link"
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          title="Open in Google Drive"
        >
          {view.links.length > 1 ? `${l.format} ↗` : '↗'}
        </a>
      ))}
      {!actionInNotice && actionButton}
      {view.settingsLink && (
        <Link
          data-testid="filing-badge-settings"
          to={view.settingsLink === 'folders' ? SETTINGS_STORAGE_PATH : SETTINGS_DRIVE_PATH}
          style={linkStyle}
        >
          {view.settingsLink === 'folders' ? 'Folders needing attention' : 'Google Drive settings'}
        </Link>
      )}
      {view.stale === 'notice' && (
        <span data-testid="filing-badge-stale" role="status" style={{ ...pillStyle, ...TONE_STYLE.warn }} className="whitespace-nowrap">
          {STALE_NOTICE}{actionInNotice && <> — {actionButton}</>}
        </span>
      )}
      {view.stale === 'revoked' && (
        <span data-testid="filing-badge-revoked" role="status" style={{ ...pillStyle, ...TONE_STYLE.warn }}>
          {REVOKED_VERSION_NOTICE}
        </span>
      )}
      {(outcome === 'conflict' || view.neutralNote) && (
        <span data-testid="filing-badge-press-outcome" role="status" style={mutedStyle}>{CONFLICT_LINE}</span>
      )}
      {outcome === 'failed' && (
        <span data-testid="filing-badge-press-outcome" role="alert" style={mutedStyle}>{PRESS_FAILED_LINE}</span>
      )}
    </span>
  );
}

// ── Styles (inline-style + CSS-var idiom, as the header's pills) ──────────────

const pillStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 9999,
};
const TONE_STYLE: Record<FilingTone, React.CSSProperties> = {
  neutral:  { background: 'var(--surface-alt)', color: 'var(--text-secondary)' },
  progress: { background: 'rgba(59,130,246,0.12)', color: '#3B82F6' },
  ok:       { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' },
  warn:     { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
  error:    { background: 'rgba(239,68,68,0.10)', color: 'var(--error, #ef4444)' },
};
const linkStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'none',
};
const actionStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '1px 8px',
  background: 'transparent', color: 'var(--primary)', border: '1px solid rgba(124,58,237,0.3)',
  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};
const mutedStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
};
